import { CXRecordLicences } from '../constants/CXRecordLicences'
import { CXScoreLicences } from '../constants/CXScoreLicences'
import { isCxsCompatibleTelSys } from './CxsSubscriptionAccessUtils'
import { isVarNotEmpty } from './GlobalFunctions'

/** What a user holds that can qualify them for Transcribe, as the user modal and the save mapper see it. */
export type TranscribeQualifiers = {
    /** The user's CX Score licence, or undefined when none is selected. */
    readonly cxsLicence: Definable<string>
    /** The user's CX Record licence, or undefined when none is selected. */
    readonly cxrLicence: Definable<string>
    /** True when CX Score is switched on for the user's device, which qualifies without a licence. */
    readonly cxsScoreEnabled: boolean
    /** True when CX Record is switched on for the user's device, which qualifies without a licence. */
    readonly cxrRecordEnabled: boolean
    /** True when the user's TelSys is BroadWorks (or a Simulator emulating it). */
    readonly isBroadWorks: boolean
}

/**
 * Whether the TelSys counts as BroadWorks for the Transcribe rule: BroadWorks itself, or a Simulator emulating it. Teams,
 * Webex and every other type are not, so CX Record alone qualifies there.
 *
 * @param telSysType The TelSys type, as a numeric id or name.
 * @param callControlOptions The TelSys {@code CallControlOptions}; only used to detect a Simulator emulating BroadWorks.
 * @returns True when only CX Score qualifies for Transcribe.
 */
export const isBroadWorksForTranscribe = (
    telSysType: Nullable<number | string>,
    callControlOptions?: Nullable<string>
): boolean => isCxsCompatibleTelSys(telSysType, callControlOptions)

/**
 * Whether a user can have Transcribe on (APD-14047 matrix, APD-14067 AC4): CX Score, or CX Record except on
 * BroadWorks, which has no CX Record-only scenario. Each of those counts whether the user holds the licence or has
 * the product switched on for their device, because the backend accepts a device-level user just the same.
 *
 * @param qualifiers The user's licences, their device-level CX Score and CX Record flags, and their TelSys.
 * @returns True when Transcribe can be offered.
 */
export const isTranscribeQualified = ({
    cxsLicence,
    cxrLicence,
    cxsScoreEnabled,
    cxrRecordEnabled,
    isBroadWorks
}: TranscribeQualifiers): boolean => {
    const hasCxScore: boolean = (isVarNotEmpty(cxsLicence) && cxsLicence !== CXScoreLicences.NONE) || cxsScoreEnabled
    const hasCxRecord: boolean = (isVarNotEmpty(cxrLicence) && cxrLicence !== CXRecordLicences.NONE) || cxrRecordEnabled

    return hasCxScore || (hasCxRecord && !isBroadWorks)
}
